import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import roleRequests from "../../api/RoleRequests";
import permissionsRequests from "../../api/PermissionRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import {
  Users,
  ArrowLeft,
  Save,
  Search,
  Check,
  Shield,
  Plus,
  ChevronDown,
  AlertCircle,
} from "lucide-react";

const EditRole = () => {
  const navigate = useNavigate();
  const { roleSlug } = useParams();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Fetch role data
  const fetchRole = async () => {
    try {
      const response = await roleRequests.getRole(roleSlug);

      if (response.data.status_code === "000") {
        setRole(response.data.data);

        const rolePermissions = response.data.data.permissions || {};
        const selectedPermissionNames = Object.values(rolePermissions).flat();
        setSelectedPermissions(selectedPermissionNames);
      } else {
        toast.error(response.message || "Failed to fetch role");
        navigate("/roles");
      }
    } catch (error) {
      toast.error("Failed to fetch role");
      navigate("/roles");
    }
  };

  // Fetch all permissions
  const fetchPermissions = async () => {
    try {
      const response = await permissionsRequests.getPermissions();

      if (response.success) {
        let permissionsData = response.data.data;

        if (Array.isArray(permissionsData)) {
          setAllPermissions(permissionsData);
        } else if (
          typeof permissionsData === "object" &&
          permissionsData !== null
        ) {
          const flattenedPermissions = Object.values(permissionsData).flat();

          setAllPermissions(flattenedPermissions);
        } else {
          setAllPermissions([]);
        }
      } else {
        console.error("Permissions API failed:", response.message);
        toast.error(response.message || "Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error("Failed to fetch permissions");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRole(), fetchPermissions()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleSlug]);

  useEffect(() => {
    if (allPermissions.length > 0) {
      const modules = [
        ...new Set(
          allPermissions.map((perm) => {
            if (perm.module || perm.module_name) {
              return perm.module || perm.module_name;
            }
            if (perm.name) {
              const nameParts = perm.name.split(".");
              if (nameParts.length > 1) {
                return (
                  nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1)
                );
              }
            }
            return "General";
          })
        ),
      ];

      setExpandedGroups(new Set(modules.slice(0, 3)));
    }
  }, [allPermissions]);

  // Handle permission selection
  const togglePermission = (permissionName) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionName)) {
        return prev.filter((name) => name !== permissionName);
      } else {
        return [...prev, permissionName];
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    //console.log("handleSubmit called", e);
    e.preventDefault();
    setSaving(true);

    try {
      // Convert selected permission names to permission slugs for the API payload
      const selectedPermissionSlugs = selectedPermissions
        .map((permissionName) => {
          const permission = allPermissions.find(
            (p) => p.name === permissionName
          );
          return permission?.permission_slug;
        })
        .filter(Boolean); // Remove any undefined values

      const payload = {
        name: role.name,
        role_slug: role.role_slug,
        permissions: selectedPermissionSlugs,
      };

      const response = await roleRequests.updateRole(payload, roleSlug);

      if (response.success) {
        toast.success("Role updated successfully!");
        navigate("/roles");
      } else {
        toast.error(response.message || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  // Filter and group permissions
  const filteredPermissions = allPermissions.filter((permission) => {
    const name =
      permission.name ||
      permission.permission_name ||
      permission.display_name ||
      "";
    const description =
      permission.description || permission.permission_description || "";
    const searchLower = permissionSearchTerm.toLowerCase();

    const matchesSearch =
      name.toLowerCase().includes(searchLower) ||
      description.toLowerCase().includes(searchLower);

    return matchesSearch;
  });

  const groupedPermissions = filteredPermissions.reduce(
    (groups, permission) => {
      let module = "General";
      const permissionName =
        permission.name ||
        permission.permission_name ||
        permission.display_name ||
        "";

      if (permission.module || permission.module_name) {
        module = permission.module || permission.module_name;
      } else if (permissionName) {
        const nameParts = permissionName.split(".");
        if (nameParts.length > 1) {
          module = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        }
      }

      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(permission);
      return groups;
    },
    {}
  );

  // Toggle group expansion
  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  if (!role) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 w-12 h-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Role not found
            </h3>
            <p className="mb-4 text-gray-500">
              The role you're looking for doesn't exist.
            </p>
            <button
              onClick={() => navigate("/roles")}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Back to Roles
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-8"
          >
            <div className="flex gap-4 items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/roles")}
                className="p-2 text-gray-600 bg-white rounded-lg border border-gray-200 transition-colors hover:text-gray-900 hover:bg-gray-50"
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Edit Role</h1>
                <p className="mt-2 text-gray-600">
                  Update role details and permissions
                </p>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Role Details Section */}
              <motion.div variants={itemVariants} className="lg:col-span-1">
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                  {/* Section Header */}
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                    <div className="flex gap-3 items-center">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Role Details
                        </h2>
                        <p className="text-sm text-gray-600">
                          Basic role information
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role Form */}
                  <div className="p-6 space-y-6">
                    {/* Role Name */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Role Name
                      </label>
                      <input
                        type="text"
                        value={role.name}
                        onChange={(e) =>
                          setRole({ ...role, name: e.target.value })
                        }
                        className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter role name"
                        required
                      />
                    </div>

                    {/* Role ID */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Role ID
                      </label>
                      <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                        <code className="font-mono text-sm text-gray-600">
                          {role.role_slug}
                        </code>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <div className="flex gap-3 items-center">
                        <span className="px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-full">
                          {role.status || "Active"}
                        </span>
                        <span className="text-sm text-gray-500">
                          Created:{" "}
                          {new Date(role.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Selected Permissions Summary */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Selected Permissions
                      </label>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-blue-900">
                            {selectedPermissions.length} permissions selected
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Permissions Section */}
              <motion.div variants={itemVariants} className="lg:col-span-2">
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                  {/* Section Header */}
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                      <div className="flex gap-3 items-center">
                        <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg shadow-sm">
                          <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            Permissions
                          </h2>
                          <p className="text-sm text-gray-600">
                            Select permissions for this role
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search permissions..."
                          value={permissionSearchTerm}
                          onChange={(e) =>
                            setPermissionSearchTerm(e.target.value)
                          }
                          className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Permissions List */}
                  <div className="p-6">
                    {Object.keys(groupedPermissions).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(groupedPermissions).map(
                          ([groupName, groupPermissions]) => (
                            <motion.div
                              key={groupName}
                              variants={itemVariants}
                              className="overflow-hidden rounded-lg border border-gray-200"
                            >
                              {/* Group Header */}
                              <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
                                <button
                                  type="button"
                                  onClick={() => toggleGroup(groupName)}
                                  className="flex flex-1 gap-3 items-center text-left"
                                >
                                  <motion.div
                                    animate={{
                                      rotate: expandedGroups.has(groupName)
                                        ? 90
                                        : 0,
                                    }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                  </motion.div>
                                  <div>
                                    <h3 className="font-semibold text-gray-900">
                                      {groupName}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {groupPermissions.length} permission
                                      {groupPermissions.length === 1 ? "" : "s"}
                                    </p>
                                  </div>
                                </button>
                                <div className="flex gap-2 items-center">
                                  <span className="text-xs text-gray-500">
                                    {
                                      groupPermissions.filter((p) =>
                                        selectedPermissions.includes(p.name)
                                      ).length
                                    }{" "}
                                    selected
                                  </span>
                                </div>
                              </div>

                              {/* Group Permissions */}
                              <AnimatePresence>
                                {expandedGroups.has(groupName) && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="p-4"
                                  >
                                    <div className="space-y-2">
                                      {groupPermissions.map((permission) => {
                                        const isSelected =
                                          selectedPermissions.includes(
                                            permission.name
                                          );
                                        return (
                                          <motion.div
                                            key={permission.name}
                                            whileHover={{ scale: 1.01 }}
                                            className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                                              isSelected
                                                ? "bg-purple-50 border-purple-200"
                                                : "bg-gray-50 border-gray-200 hover:border-purple-200"
                                            }`}
                                            onClick={() =>
                                              togglePermission(permission.name)
                                            }
                                          >
                                            <div className="flex justify-between items-center">
                                              <div className="flex gap-3 items-center">
                                                <div
                                                  className={`p-2 rounded-lg ${
                                                    isSelected
                                                      ? "bg-purple-100"
                                                      : "bg-gray-100"
                                                  }`}
                                                >
                                                  <Shield
                                                    className={`w-4 h-4 ${
                                                      isSelected
                                                        ? "text-purple-600"
                                                        : "text-gray-500"
                                                    }`}
                                                  />
                                                </div>
                                                <div>
                                                  <h4 className="font-medium text-gray-900">
                                                    {permission.name ||
                                                      permission.permission_name ||
                                                      permission.display_name}
                                                  </h4>
                                                  {permission.description && (
                                                    <p className="text-sm text-gray-500">
                                                      {permission.description}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex gap-2 items-center">
                                                {isSelected ? (
                                                  <div className="p-1 bg-purple-100 rounded-full">
                                                    <Check className="w-4 h-4 text-purple-600" />
                                                  </div>
                                                ) : (
                                                  <div className="p-1 bg-gray-100 rounded-full">
                                                    <Plus className="w-4 h-4 text-gray-400" />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </motion.div>
                                        );
                                      })}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="py-12 text-center">
                        <AlertCircle className="mx-auto mb-4 w-12 h-12 text-gray-400" />
                        <h3 className="mb-2 text-lg font-medium text-gray-900">
                          No permissions found
                        </h3>
                        <p className="text-gray-500">
                          {permissionSearchTerm
                            ? "Try adjusting your search criteria."
                            : "No permissions are available."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Action Buttons at the bottom of the form */}
            <div className="flex flex-col gap-3 mt-10 sm:flex-row sm:justify-end sm:items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => navigate("/roles")}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
                disabled={saving}
                className="flex gap-2 justify-center items-center px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditRole;
